import React, { useEffect, useState } from 'react'
import { db } from '../../../firebase'
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore'
import Icon from '../../../components/AppIcon'
import { toDate } from '../lib/goldTx'

const ACTION_META = {
  transaction_created: { icon: 'Receipt', label: 'Transaction created', tone: 'text-primary bg-primary/10' },
  envelope_report_confirmed: { icon: 'FileCheck', label: 'Report confirmed manually', tone: 'text-primary bg-primary/10' },
  envelope_report_started: { icon: 'Send', label: 'Report sending', tone: 'text-primary bg-primary/10' },
  envelope_report_sent: { icon: 'FileCheck', label: 'Report sent · hold started', tone: 'text-success bg-success/10' },
  envelope_report_failed: { icon: 'AlertCircle', label: 'Report not completed', tone: 'text-destructive bg-destructive/10' },
  envelope_claimed: { icon: 'PackageCheck', label: 'Envelope claimed', tone: 'text-success bg-success/10' },
  settings_updated: { icon: 'Settings', label: 'Settings updated', tone: 'text-warning bg-warning/10' },
  contract_template_updated: { icon: 'FileText', label: 'Contract template updated', tone: 'text-warning bg-warning/10' },
}

const fmtWhen = (d) =>
  d ? d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'

const summarize = (details = {}) =>
  Object.entries(details)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v?.toDate === 'function' || v instanceof Date ? fmtWhen(toDate(v)) : typeof v === 'number' ? v.toLocaleString('en-US', { maximumFractionDigits: 2 }) : v}`)
    .join(' · ')

/**
 * Business audit trail viewer (gold_audit_logs — append-only; clients can
 * create most actions but never edit/delete; confirmation and claims are server-only).
 */
export default function ExchangeAuditLog() {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const snap = await getDocs(query(collection(db, 'gold_audit_logs'), orderBy('createdAt', 'desc'), limit(300)))
        if (alive) setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load audit log')
      }
    })()
    return () => { alive = false }
  }, [])

  if (error) {
    return <div className="bg-card rounded-2xl border border-border p-6 text-sm text-destructive">{error}</div>
  }
  if (!rows) {
    return <div className="bg-card rounded-2xl border border-border p-8 text-center text-muted-foreground text-sm">Loading audit log…</div>
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
          <Icon name="ScrollText" size={22} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Audit Log</h2>
          <p className="text-sm text-muted-foreground">
            Who did what and when — transactions, report deliveries, claims and settings
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground bg-muted/20 border border-border rounded-xl p-5">
          No events recorded yet. Events are logged from now on — new purchases,
          envelope claims and settings changes will show up here.
        </p>
      ) : (
        <div className="divide-y divide-border/60">
          {rows.map((r) => {
            const meta = ACTION_META[r.action] || { icon: 'Activity', label: r.action, tone: 'text-muted-foreground bg-muted/40' }
            return (
              <div key={r.id} className="py-3 flex items-start gap-3">
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${meta.tone}`}>
                  <Icon name={meta.icon} size={13} />
                  {meta.label}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground truncate">{summarize(r.details) || '—'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {fmtWhen(toDate(r.createdAt))}{r.userEmail ? ` · ${r.userEmail}` : ''}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
